// TanStack Query 5: QueryClient з розумними дефолтами + тост на помилку мутації.
// Вимкнути тост для мутації: useMutation({ …, meta: { silent: true } }); власний заголовок — meta: { errorMessage: '…' };
// тост успіху — meta: { successMessage: 'Збережено' }. Для запиту тост на помилку — meta: { errorToast: true }.
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ApiError, errorMessage } from './api';
import { toast } from './toast';

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: Error;
    queryMeta: { errorToast?: boolean; errorMessage?: string };
    mutationMeta: { silent?: boolean; errorMessage?: string; successMessage?: string };
  }
}

function shouldRetry(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError) {
    // 4xx (крім 408/429) не повторюємо; мережеві/5xx — один раз.
    if (error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) return false;
  }
  return failureCount < 1;
}

/** 401 після невдалого refresh обробляє AuthProvider (перехід на /login) — без тосту. */
function isSilentAuthError(error: Error): boolean {
  return error instanceof ApiError && error.status === 401;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!query.meta?.errorToast || isSilentAuthError(error)) return;
        toast.error({ title: query.meta.errorMessage ?? 'Не вдалося завантажити дані', description: errorMessage(error) });
      },
    }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _vars, _ctx, mutation) => {
        const msg = mutation.meta?.successMessage;
        if (msg) toast.success(msg);
      },
      onError: (error, _vars, _ctx, mutation) => {
        if (mutation.meta?.silent || isSilentAuthError(error)) return;
        const title = mutation.meta?.errorMessage;
        if (title) toast.error({ title, description: errorMessage(error) });
        else toast.error(errorMessage(error));
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: shouldRetry,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

export const queryClient = createQueryClient();

/** Ключі запитів спільних даних шелла (модулі можуть інвалідувати). */
export const queryKeys = {
  me: ['auth', 'me'] as const,
  semesters: ['directories', 'semesters', 'items', 'shell'] as const,
  directories: ['directories'] as const,
  directory: (code: string) => ['directories', code] as const,
};
